import React, { useEffect, useState } from 'react';
import api from '../services/api';

const pnlColor = (v: number) => v >= 0 ? '#2ed573' : '#ff6b6b';
const fmt = (v: number) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const RuleStatisticsPage: React.FC = () => {
  const [rules, setRules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRuleStats = async () => {
    try {
      const [rulesRes, statsRes, accountsRes] = await Promise.all([
        api.get('/v1/rules'),
        api.get('/v1/statistics/broker-pnl'),
        api.get('/v1/accounts'),
      ]);

      const ruleList = rulesRes.data;
      const allStats: any[] = statsRes.data.accounts || [];
      const allAccounts: any[] = accountsRes.data;

      // accountId → ruleId 매핑
      const accountRuleMap: Record<string, number> = {};
      allAccounts.forEach((acc: any) => {
        if (acc.ruleId) accountRuleMap[acc.accountId] = acc.ruleId;
      });

      // 룰별로 계좌 통계 묶기
      const ruleStats = ruleList.map((rule: any) => {
        const ruleAccounts = allStats.filter((acc: any) => accountRuleMap[acc.accountId] === rule.id);

        const totalBBookPnl = ruleAccounts.reduce((s: number, a: any) => s + (Number(a.bBookBrokerPnl) || 0), 0);
        const totalABookPnl = ruleAccounts.reduce((s: number, a: any) => s + (Number(a.aBookNetPnl) || 0), 0);
        const totalAgentPnl = ruleAccounts.reduce((s: number, a: any) => s + (Number(a.agentPnl) || 0), 0);
        const totalBrokerPnl = ruleAccounts.reduce((s: number, a: any) => s + (Number(a.brokerTotalPnl) || 0), 0);
        const totalFloating = ruleAccounts.reduce((s: number, a: any) => s + (Number(a.floatingPnl) || 0), 0);

        return {
          id: rule.id,
          name: rule.name,
          thresholdEntry: rule.thresholdEntry,
          thresholdReturn: rule.thresholdReturn,
          reEntryThreshold: rule.reEntryThreshold,
          accountCount: ruleAccounts.length,
          totalBBookPnl,
          totalABookPnl,
          totalAgentPnl,
          totalBrokerPnl,
          totalFloating,
          accounts: ruleAccounts,
        };
      });

      setRules(ruleStats);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRuleStats();
    const interval = setInterval(fetchRuleStats, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>⚙️ 전환 룰별 수익</h1>
          <p style={styles.subtitle}>룰별 브로커 손익 현황 · 30초마다 자동 갱신</p>
        </div>
        <button style={styles.refreshBtn} onClick={fetchRuleStats}>🔄 새로고침</button>
      </div>

      {loading ? (
        <div style={styles.loading}>로딩 중...</div>
      ) : (
        <div>
          {rules.map((rule: any) => (
            <div key={rule.id} style={styles.ruleCard}>
              {/* 룰 헤더 */}
              <div style={styles.ruleHeader}>
                <div>
                  <span style={styles.ruleName}>{rule.name}</span>
                  <span style={styles.ruleInfo}>
                    전환 {rule.thresholdEntry}% · 복귀 {rule.thresholdReturn === 0 ? '청산 시' : `${rule.thresholdReturn}%`} · 재진입 {rule.reEntryThreshold}%
                  </span>
                </div>
                <div style={styles.ruleSummary}>
                  <div style={styles.ruleSummaryItem}>
                    <span style={styles.ruleSummaryLabel}>B북 수익</span>
                    <span style={{ ...styles.ruleSummaryValue, color: pnlColor(rule.totalBBookPnl) }}>{fmt(rule.totalBBookPnl)}</span>
                  </div>
                  <div style={styles.ruleSummaryItem}>
                    <span style={styles.ruleSummaryLabel}>A북 수익</span>
                    <span style={{ ...styles.ruleSummaryValue, color: pnlColor(rule.totalABookPnl) }}>{fmt(rule.totalABookPnl)}</span>
                  </div>
                  <div style={styles.ruleSummaryItem}>
                    <span style={styles.ruleSummaryLabel}>Agent 비용</span>
                    <span style={{ ...styles.ruleSummaryValue, color: '#ff6b6b' }}>{fmt(-Math.abs(rule.totalAgentPnl))}</span>
                  </div>
                  <div style={styles.ruleSummaryItem}>
                    <span style={styles.ruleSummaryLabel}>미실현</span>
                    <span style={{ ...styles.ruleSummaryValue, color: pnlColor(rule.totalFloating) }}>{fmt(rule.totalFloating)}</span>
                  </div>
                  <div style={styles.ruleSummaryItem}>
                    <span style={styles.ruleSummaryLabel}>브로커 순손익</span>
                    <span style={{ ...styles.ruleSummaryValue, color: pnlColor(rule.totalBrokerPnl), fontSize: '18px' }}>
                      {fmt(rule.totalBrokerPnl)}
                    </span>
                  </div>
                  <div style={styles.ruleSummaryItem}>
                    <span style={styles.ruleSummaryLabel}>계좌수</span>
                    <span style={{ ...styles.ruleSummaryValue, color: '#667eea' }}>{rule.accountCount}</span>
                  </div>
                </div>
              </div>

              {/* 계좌 테이블 */}
              {rule.accounts.length > 0 && (
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.thead}>
                      <th style={styles.th}>계좌번호</th>
                      <th style={styles.th}>이름</th>
                      <th style={styles.th}>구분</th>
                      <th style={styles.th}>B북 수익</th>
                      <th style={styles.th}>A북 수익</th>
                      <th style={styles.th}>Agent 비용</th>
                      <th style={styles.th}>브로커 순손익</th>
                      <th style={styles.th}>미실현</th>
                      <th style={styles.th}>거래수</th>
                      <th style={styles.th}>전환횟수</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rule.accounts.map((acc: any, idx: number) => (
                      <tr key={acc.accountId} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                        <td style={styles.td}><span style={styles.accountId}>{acc.accountId}</span></td>
                        <td style={styles.td}><span style={styles.name}>{acc.name || '-'}</span></td>
                        <td style={styles.td}>
                          <span style={acc.bookType === 'A' ? styles.badgeA : styles.badgeB}>
                            {acc.bookType === 'A' ? '🔴 A북' : '🟢 B북'}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ fontFamily: 'monospace', color: pnlColor(Number(acc.bBookBrokerPnl) || 0) }}>
                            {fmt(Number(acc.bBookBrokerPnl) || 0)}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ fontFamily: 'monospace', color: pnlColor(Number(acc.aBookNetPnl) || 0) }}>
                            {fmt(Number(acc.aBookNetPnl) || 0)}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ fontFamily: 'monospace', color: '#ff6b6b' }}>
                            {fmt(-Math.abs(Number(acc.agentPnl) || 0))}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ fontFamily: 'monospace', fontWeight: 700, color: pnlColor(Number(acc.brokerTotalPnl) || 0) }}>
                            {fmt(Number(acc.brokerTotalPnl) || 0)}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ fontFamily: 'monospace', color: pnlColor(Number(acc.floatingPnl) || 0) }}>
                            {fmt(Number(acc.floatingPnl) || 0)}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span style={styles.mono}>{(Number(acc.bBookTradeCount) || 0).toLocaleString()}건</span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ color: (Number(acc.aBookSwitchCount) || 0) > 0 ? '#f7971e' : 'rgba(255,255,255,0.3)' }}>
                            {acc.aBookSwitchCount || 0}회
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {rule.accounts.length === 0 && (
                <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '13px' }}>
                  이 룰에 등록된 계좌가 없습니다.
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { color: '#fff', width: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontSize: '28px', fontWeight: 700, color: '#fff', margin: '0 0 4px' },
  subtitle: { color: 'rgba(255,255,255,0.4)', fontSize: '13px', margin: 0 },
  refreshBtn: { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '8px 16px', color: 'rgba(255,255,255,0.7)', fontSize: '13px', cursor: 'pointer' },
  ruleCard: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', marginBottom: '20px', overflow: 'hidden' },
  ruleHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexWrap: 'wrap' as const, gap: '16px' },
  ruleName: { fontSize: '18px', fontWeight: 700, color: '#a78bfa', marginRight: '12px' },
  ruleInfo: { fontSize: '12px', color: 'rgba(255,255,255,0.4)' },
  ruleSummary: { display: 'flex', gap: '24px', flexWrap: 'wrap' as const },
  ruleSummaryItem: { display: 'flex', flexDirection: 'column' as const, alignItems: 'flex-end' },
  ruleSummaryLabel: { fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '2px' },
  ruleSummaryValue: { fontSize: '15px', fontWeight: 700, fontFamily: 'monospace' },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  thead: { background: 'rgba(255,255,255,0.03)' },
  th: { padding: '12px 16px', textAlign: 'left' as const, fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.4)', borderBottom: '1px solid rgba(255,255,255,0.06)', whiteSpace: 'nowrap' as const },
  trEven: { background: 'transparent' },
  trOdd: { background: 'rgba(255,255,255,0.02)' },
  td: { padding: '12px 16px', fontSize: '13px', borderBottom: '1px solid rgba(255,255,255,0.04)', verticalAlign: 'middle' as const },
  accountId: { fontFamily: 'monospace', fontSize: '13px', fontWeight: 700, color: '#667eea' },
  name: { color: 'rgba(255,255,255,0.8)', fontSize: '13px' },
  mono: { fontFamily: 'monospace', fontSize: '13px', color: 'rgba(255,255,255,0.7)' },
  badgeA: { background: 'rgba(255,107,107,0.15)', color: '#ff6b6b', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 },
  badgeB: { background: 'rgba(46,213,115,0.15)', color: '#2ed573', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 },
  loading: { padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.4)' },
};

export default RuleStatisticsPage;
